import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import {
  params, string, number, shape, arrayOf, bool, extend
} from '@thd-nucleus/data-sources';
import { useProductDeprecated } from '@thd-nucleus/data-sources/react/dataModel/migration';
import { LoadingPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import { Promotion } from '@thd-olt-component-react/promotion';
import PriceSimple from './partials/PriceSimple';
import PriceDetailed from './partials/PriceDetailed';
import Sku from '../models/Sku';
import helpers, { PRICE_TYPES, PRODUCT_DOMAINS } from '../helpers/price-helper';

export const getDisplayedPrice = (product) => {

  const priceObj = {
    product
  };
  const { _price, type } = helpers.getPricingObject(priceObj);
  const {
    price = '0',
    wasPrice,
    alternatePrice,
    mapPrice,
    pricingMessage
  } = _price;
  let retPrice = '0';

  switch (type) {
    case PRICE_TYPES.STANDARD:
    case PRICE_TYPES.STARTING_AT:
    case PRICE_TYPES.NO_DETAILS:
      return price;

    case PRICE_TYPES.STRIKE_THROUGH:
      return wasPrice.special;

    case PRICE_TYPES.ECO_REBATE:
      if (mapPrice || pricingMessage) {
        return '0';
      }
      if (!mapPrice) {
        retPrice = wasPrice ? wasPrice.special : price;
        return retPrice;
      }
      return '0';

    case PRICE_TYPES.INSTANT_REBATE:
    case PRICE_TYPES.BULK_PRICE:
    case PRICE_TYPES.LIMIT_PER_ORDER:
      retPrice = wasPrice ? wasPrice.special : price;
      return retPrice;

    case PRICE_TYPES.UNIT_AND_CASE:
      return alternatePrice;

    case PRICE_TYPES.MAP_PRICE:
    case PRICE_TYPES.BRIO:
    case PRICE_TYPES.LIVE_GOODS:
      return '0';

    case PRICE_TYPES.RANGE_PRICE:
      return price;

    default: return '0';
  }
};

const Price = (props) => {
  const { channel, isServer } = useContext(ExperienceContext);
  const { storeId: defaultStoreId, storeZip: defaultStoreZip } = useStore();
  const {
    basic,
    disableRangePricing,
    displayEachUom,
    hideBadge,
    hideBulkPrice,
    hideConditionalPromoDescription,
    hideLimitPerOrder,
    hidePromotions,
    hideSavingsText,
    itemId,
    large,
    omitDetails,
    onSavingsCenterToggle,
    /* eslint-disable-next-line react/prop-types */
    product,
    skip,
    storeId = defaultStoreId,
    zipCode = defaultStoreZip
  } = props;

  // const { data, loading, error } = useDataModel('product', { variables: { itemId } });
  const { data, loading, error } = useProductDeprecated({
    itemId,
    domains: PRODUCT_DOMAINS,
    storeId,
    skip: !!product,
    ssr: !skip
  });

  if (skip && isServer) return null;

  if (loading) {
    return (
      <LoadingPlaceholder
        type="text"
        ready={false}
        color="#E0E0E0"
        rows={1}
        showLoadingAnimation
      >
        Loading...
      </LoadingPlaceholder>
    );
  }

  const productToBeUsed = product || data?.product;
  if (!productToBeUsed) {
    return null;
  }

  const sku = new Sku(productToBeUsed);

  return (
    <>
      {large && (
        <PriceDetailed
          channel={channel}
          disableRangePricing={disableRangePricing}
          displayEachUom={displayEachUom}
          hideBadge={hideBadge}
          hideBulkPrice={hideBulkPrice}
          hideConditionalPromoDescription={hideConditionalPromoDescription}
          hideLimitPerOrder={hideLimitPerOrder}
          hidePromotions={hidePromotions}
          hideSavingsText={hideSavingsText}
          omitDetails={omitDetails}
          product={sku}
          storeId={storeId}
          zipCode={zipCode}
        />
      )}
      {!large && (
        <PriceSimple
          basic={basic}
          channel={channel}
          disableRangePricing={disableRangePricing}
          displayEachUom={displayEachUom}
          hideBadge={hideBadge}
          hideBulkPrice={hideBulkPrice}
          hideLimitPerOrder={hideLimitPerOrder}
          hideSavingsText={hideSavingsText}
          omitDetails={omitDetails}
          onSavingsCenterToggle={onSavingsCenterToggle}
          product={sku}
          zipCode={zipCode}
        />
      )}
    </>
  );
};

Price.propTypes = {
  basic: PropTypes.bool,
  disableRangePricing: PropTypes.bool,
  displayEachUom: PropTypes.bool,
  hideBadge: PropTypes.bool,
  hideBulkPrice: PropTypes.bool,
  hideConditionalPromoDescription: PropTypes.bool,
  hideLimitPerOrder: PropTypes.bool,
  hidePromotions: PropTypes.bool,
  hideSavingsText: PropTypes.bool,
  itemId: PropTypes.string.isRequired,
  large: PropTypes.bool,
  omitDetails: PropTypes.bool,
  onSavingsCenterToggle: PropTypes.func,
  skip: PropTypes.bool,
  storeId: PropTypes.string,
  zipCode: PropTypes.string
};

Price.defaultProps = {
  basic: false,
  disableRangePricing: false,
  displayEachUom: false,
  hideBadge: false,
  hideBulkPrice: false,
  hideConditionalPromoDescription: false,
  hideLimitPerOrder: false,
  hidePromotions: false,
  hideSavingsText: false,
  large: true,
  omitDetails: false,
  onSavingsCenterToggle: () => {},
  skip: false,
  storeId: undefined,
  zipCode: undefined
};

Price.dataModel = extend({
  product: params({ itemId: string().isRequired(), dataSource: string() }).shape({
    itemId: string(),
    dataSources: string(),
    identifiers: shape({
      itemId: string()
    }),
    info: shape({
      hidePrice: bool(),
      ecoRebate: bool(),
      quantityLimit: number(),
      sskMin: string(),
      sskMax: string(),
      unitOfMeasureCoverage: string(),
      wasMaxPriceRange: number({ float: true }),
      wasMinPriceRange: number({ float: true })
    }),
    media: shape({
      images: arrayOf(shape({
        url: string(),
        type: string(),
        subType: string(),
        sizes: arrayOf(string())
      }))
    }),
    pricing: params({ storeId: string() }).shape({
      alternatePriceDisplay: bool(),
      alternate: shape({
        bulk: shape({
          pricePerUnit: number({ float: true }),
          thresholdQuantity: number(),
          value: number({ float: true })
        }),
        unit: {
          caseUnitOfMeasure: string(),
          unitsOriginalPrice: number({ float: true }),
          unitsPerCase: number({ float: true }),
          value: number({ float: true }),
        },
      }),
      original: number({ float: true }),
      mapAboveOriginalPrice: bool(),
      message: string(),
      promotion: shape({
        type: string(),
        description: shape({
          shortDesc: string(),
          longDesc: string()
        }),
        dollarOff: number({ float: true }),
        percentageOff: number({ float: true }),
        savingsCenter: string(),
        savingsCenterPromos: string(),
        specialBuySavings: string(),
        specialBuyDollarOff: number({ float: true }),
        specialBuyPercentageOff: number({ float: true })
      }),
      specialBuy: number({ float: true }),
      unitOfMeasure: string(),
      value: number({ float: true }),
    }),
  })
},
Promotion
);

export default Price;
